/**
 * Hand-built SVG line chart of cumulative points per matchday for each league member.
 *
 * Pure geometry comes from helpers/bumpChartGeometry.ts; this component only renders and
 * wires interaction (multi-select line highlight, matchday selection, playback clip via
 * `visibleThrough`). Pattern mirrors components/knockout/KnockoutBracketLayer.tsx.
 */
import { useMemo } from 'react';
import { Box, useColorMode, useColorModeValue } from '@chakra-ui/react';

import {
  buildLinePath,
  createChartScales,
  DEFAULT_CHART_DIMS,
  lineTip,
  matchdayTicks,
  maxPointsAcross,
  pointTicks,
  visibleVertices,
} from '@/helpers/leagues/bumpChartGeometry';
import {
  type ChartStageBandId,
  chartStageBandXBounds,
  resolveChartStageBands,
} from '@/helpers/leagues/chartStageBands';
import type { UserSeries } from '@/helpers/leagues/standingsSeries';
import { chartColor } from './chartPalette';

const STAGE_BAND_FILL: Record<ChartStageBandId, { light: string; dark: string }> = {
  G1: { light: 'rgba(43,138,62,0.07)', dark: 'rgba(81,207,102,0.09)' },
  G2: { light: 'rgba(12,133,153,0.07)', dark: 'rgba(59,201,219,0.09)' },
  G3: { light: 'rgba(92,148,13,0.07)', dark: 'rgba(169,227,75,0.08)' },
  R32: { light: 'rgba(47,111,237,0.07)', dark: 'rgba(91,141,239,0.09)' },
  R16: { light: 'rgba(24,100,171,0.07)', dark: 'rgba(116,192,252,0.09)' },
  Finals: { light: 'rgba(156,54,181,0.07)', dark: 'rgba(204,93,232,0.09)' },
};

export interface StandingsChartProps {
  series: UserSeries[];
  maxMatchday: number;
  visibleThrough: number;
  /** Head-to-head filter: when non-empty, only these uids are emphasised. */
  selectedUids: ReadonlySet<string>;
  /** Temporary single-line preview when no filter is active. */
  hoveredUid: string | null;
  /** The signed-in user's uid, for a subtle always-on emphasis. */
  currentUid?: string;
  selectedMatchday: number | null;
  /** uid -> palette index (stable colour per user). */
  colorIndex: Map<string, number>;
  onHoverUid: (uid: string | null) => void;
  onClickUid: (uid: string) => void;
  onSelectMatchday: (matchday: number) => void;
}

function lineStyle(
  uid: string,
  hasFilter: boolean,
  hoveredUid: string | null,
  selectedUids: ReadonlySet<string>,
  currentUid: string | undefined,
): { opacity: number; strokeWidth: number; isEmphasized: boolean } {
  const isSelected = hasFilter && selectedUids.has(uid);
  const isHoverPreview = !hasFilter && hoveredUid === uid;
  const isEmphasized = isSelected || isHoverPreview;
  const isCurrent = currentUid === uid;
  const opacity = !hasFilter && !hoveredUid ? 0.95 : isEmphasized ? 1 : 0.12;
  const strokeWidth = isEmphasized ? 3.6 : isCurrent && !hasFilter && !hoveredUid ? 2.6 : 1.8;
  return { opacity, strokeWidth, isEmphasized };
}

export function StandingsChart(props: StandingsChartProps) {
  const { colorMode } = useColorMode();
  const gridStroke = useColorModeValue('rgba(13,11,45,0.08)', 'rgba(255,255,255,0.08)');
  const axisText = useColorModeValue('#4A5568', '#A0AEC0');
  const selectedBand = useColorModeValue('rgba(39,173,228,0.12)', 'rgba(25,200,245,0.14)');
  const labelBg = useColorModeValue('rgba(255,255,255,0.85)', 'rgba(13,11,45,0.85)');

  const { width, height } = DEFAULT_CHART_DIMS;

  const scales = useMemo(
    () => createChartScales(DEFAULT_CHART_DIMS, props.maxMatchday, maxPointsAcross(props.series)),
    [props.maxMatchday, props.series],
  );

  const xTicks = useMemo(() => matchdayTicks(scales), [scales]);
  const yTicks = useMemo(() => pointTicks(scales), [scales]);
  const stageBands = useMemo(() => resolveChartStageBands(props.maxMatchday), [props.maxMatchday]);

  const hasFilter = props.selectedUids.size > 0;
  const showHoverPreview = !hasFilter && props.hoveredUid != null;

  // Draw emphasised lines last so they sit on top of faded ones.
  const renderOrder = useMemo(() => {
    if (!hasFilter && !showHoverPreview) return props.series;
    if (hasFilter) {
      const others = props.series.filter((s) => !props.selectedUids.has(s.uid));
      const selected = props.series.filter((s) => props.selectedUids.has(s.uid));
      return [...others, ...selected];
    }
    const others = props.series.filter((s) => s.uid !== props.hoveredUid);
    const hovered = props.series.filter((s) => s.uid === props.hoveredUid);
    return [...others, ...hovered];
  }, [props.series, props.selectedUids, props.hoveredUid, hasFilter, showHoverPreview]);

  const labelSeries = useMemo(() => {
    if (hasFilter) {
      return props.series.filter((s) => props.selectedUids.has(s.uid));
    }
    if (showHoverPreview) {
      return props.series.filter((s) => s.uid === props.hoveredUid);
    }
    return [];
  }, [props.series, props.selectedUids, props.hoveredUid, hasFilter, showHoverPreview]);

  const baselineY = scales.yScale(0);

  return (
    <Box
      as='svg'
      viewBox={`0 0 ${width} ${height}`}
      width='100%'
      height='auto'
      style={{ touchAction: 'none', userSelect: 'none' }}
      role='img'
      aria-label='League points by matchday'
    >
      {/* Tournament stage background bands */}
      <g aria-hidden='true' pointerEvents='none'>
        {stageBands.map((band) => {
          const { x1, x2 } = chartStageBandXBounds(scales, band.mdStart, band.effectiveEnd);
          const fill = STAGE_BAND_FILL[band.id][colorMode];
          const labelX = (x1 + x2) / 2;
          return (
            <g key={band.id}>
              <rect
                x={x1}
                y={scales.dims.margin.top}
                width={x2 - x1}
                height={baselineY - scales.dims.margin.top}
                fill={fill}
              />
              <text
                x={labelX}
                y={scales.dims.margin.top + 10}
                fontSize={9}
                fill={axisText}
                fillOpacity={0.45}
                textAnchor='middle'
                dominantBaseline='middle'
              >
                {band.label}
              </text>
            </g>
          );
        })}
      </g>

      {/* Horizontal point gridlines + y labels */}
      {yTicks.map((tick) => (
        <g key={`y-${tick.value}`}>
          <line
            x1={scales.dims.margin.left}
            x2={width - scales.dims.margin.right}
            y1={tick.pos}
            y2={tick.pos}
            stroke={gridStroke}
            strokeWidth={1}
          />
          <text
            x={scales.dims.margin.left - 8}
            y={tick.pos}
            fontSize={11}
            fill={axisText}
            textAnchor='end'
            dominantBaseline='middle'
          >
            {tick.label}
          </text>
        </g>
      ))}

      {/* Selected matchday highlight band */}
      {props.selectedMatchday != null && props.selectedMatchday >= 1 ? (
        <line
          x1={scales.xScale(props.selectedMatchday)}
          x2={scales.xScale(props.selectedMatchday)}
          y1={scales.dims.margin.top}
          y2={baselineY}
          stroke={selectedBand}
          strokeWidth={14}
          strokeLinecap='round'
        />
      ) : null}

      {/* Matchday ticks + clickable hit bands + x labels */}
      {xTicks.map((tick) => {
        const bandWidth = scales.innerWidth / Math.max(1, scales.maxMatchday);
        return (
          <g key={`x-${tick.value}`}>
            <line
              x1={tick.pos}
              x2={tick.pos}
              y1={scales.dims.margin.top}
              y2={baselineY}
              stroke={gridStroke}
              strokeWidth={1}
            />
            <text x={tick.pos} y={baselineY + 18} fontSize={11} fill={axisText} textAnchor='middle'>
              {tick.label}
            </text>
            {/* biome-ignore lint/a11y/useSemanticElements: an SVG <rect> cannot be a native <button>; role + keyboard handler provided */}
            <rect
              x={tick.pos - bandWidth / 2}
              y={scales.dims.margin.top}
              width={bandWidth}
              height={baselineY - scales.dims.margin.top}
              fill='transparent'
              role='button'
              tabIndex={0}
              aria-label={`Show MatchDay ${tick.value} detail`}
              style={{ cursor: 'pointer' }}
              onClick={() => props.onSelectMatchday(tick.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  props.onSelectMatchday(tick.value);
                }
              }}
            >
              <title>{`MatchDay ${tick.value}`}</title>
            </rect>
          </g>
        );
      })}

      {/* User lines */}
      {renderOrder.map((s) => {
        const idx = props.colorIndex.get(s.uid) ?? 0;
        const stroke = chartColor(idx, colorMode);
        const { opacity, strokeWidth } = lineStyle(
          s.uid,
          hasFilter,
          props.hoveredUid,
          props.selectedUids,
          props.currentUid,
        );
        const d = buildLinePath(s, scales, props.visibleThrough);
        if (!d) return null;
        return (
          // biome-ignore lint/a11y/useSemanticElements: an SVG <g> cannot be a native <button>; the legend offers an accessible equivalent
          <g
            key={s.uid}
            role='button'
            tabIndex={0}
            aria-label={`Highlight ${s.name}`}
            onMouseEnter={() => props.onHoverUid(s.uid)}
            onMouseLeave={() => props.onHoverUid(null)}
            onFocus={() => props.onHoverUid(s.uid)}
            onBlur={() => props.onHoverUid(null)}
            onClick={() => props.onClickUid(s.uid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onClickUid(s.uid);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {/* Invisible fat hit-line for easy hover/click */}
            <path d={d} fill='none' stroke='transparent' strokeWidth={12} style={{ pointerEvents: 'stroke' }} />
            <path
              d={d}
              fill='none'
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinejoin='round'
              strokeLinecap='round'
              opacity={opacity}
            />
          </g>
        );
      })}

      {/* Vertex markers + end labels for selected or hover-preview lines */}
      {labelSeries.map((s) => {
        const idx = props.colorIndex.get(s.uid) ?? 0;
        const stroke = chartColor(idx, colorMode);
        const verts = visibleVertices(s, scales, props.visibleThrough);
        const tip = lineTip(s, scales, props.visibleThrough);
        const labelWidth = Math.min(120, 8 + s.name.length * 6.2);
        return (
          <g key={`marker-${s.uid}`} style={{ pointerEvents: 'none' }}>
            {verts.map((v) => (
              <circle key={`${s.uid}-${v.matchday}`} cx={v.x} cy={v.y} r={2.6} fill={stroke} />
            ))}
            {tip ? (
              <>
                <circle cx={tip.x} cy={tip.y} r={4} fill={stroke} />
                <rect x={tip.x + 7} y={tip.y - 9} width={labelWidth} height={18} rx={4} fill={labelBg} />
                <text
                  x={tip.x + 12}
                  y={tip.y}
                  fontSize={11}
                  fontWeight={600}
                  fill={stroke}
                  dominantBaseline='middle'
                >
                  {s.name}
                </text>
              </>
            ) : null}
          </g>
        );
      })}
    </Box>
  );
}
